import { perTradeCostReference } from '../data';

function parseAmount(amount: string): number {
  return parseFloat(amount.replace('$', '').replace(/,/g, ''));
}

function extractPctNum(s: string): number {
  const m = s.match(/([\d.]+)%/);
  return m ? parseFloat(m[1]) : 0;
}

type Row = {
  strategy: string;
  trader: string;
  lotSize: string;
  spread: string;
  commission: string;
  swap: string;
  swapNoteMark: number | null;
  slippage: string;
  total: string;
  breakEven: string;
  target: string;
  burden: string;
  burdenNum: number;
};

const footnotes: { mark: number; text: string }[] = [];
let markCounter = 0;

const rows: Row[] = perTradeCostReference.map((item) => {
  const find = (kw: string) =>
    item.costs.find((c) => c.name.toLowerCase().includes(kw));

  const spread = find('spread');
  const commission = find('commission');
  const slippage = find('slippage');
  const swapPrimary = item.costs.find(
    (c) => c.name.toLowerCase().includes('swap') && parseAmount(c.amount) > 0
  );
  const swapAny = item.costs.find((c) => c.name.toLowerCase().includes('swap'));

  let swapNoteMark: number | null = null;
  if ('swapNote' in item && item.swapNote) {
    markCounter += 1;
    swapNoteMark = markCounter;
    footnotes.push({ mark: markCounter, text: item.swapNote });
  }

  return {
    strategy: item.strategy,
    trader: item.trader,
    lotSize: item.lotSize,
    spread: spread ? spread.amount : '—',
    commission: commission ? commission.amount : '—',
    swap: swapPrimary ? swapPrimary.amount : swapAny ? '$0' : '—',
    swapNoteMark,
    slippage: slippage ? slippage.amount : '—',
    total: item.totalPerTrade,
    breakEven: item.breakEvenPips,
    target: item.targetPips,
    burden: item.costEatsPct.replace(/\s*of target\s*/i, ''),
    burdenNum: extractPctNum(item.costEatsPct),
  };
});

const maxBurden = Math.max(...rows.map((r) => r.burdenNum));

const costRows: { label: string; key: 'spread' | 'commission' | 'swap' | 'slippage' }[] = [
  { label: 'Spread', key: 'spread' },
  { label: 'Commission', key: 'commission' },
  { label: 'Swap', key: 'swap' },
  { label: 'Slippage', key: 'slippage' },
];

function ValueCell({ value }: { value: string }) {
  const muted = value === '—' || value === '$0';
  return (
    <span className={`tabular-nums ${muted ? 'text-foreground-300' : 'text-foreground-900'}`}>
      {value}
    </span>
  );
}

/* ── Mobile strategy card ── */
function StrategyCard({ r, index }: { r: Row; index: number }) {
  return (
    <div className="rounded-xl border border-background-200/80 overflow-hidden bg-background-50">
      {/* Card header */}
      <div className="px-4 py-3.5 border-b border-background-200/60 bg-background-100/60">
        <div className="text-[14px] font-[750] text-foreground-950 leading-tight">{r.strategy}</div>
        <div className="text-[11px] text-foreground-500 mt-0.5">{r.trader} · {r.lotSize} / trade</div>
      </div>

      {/* Cost rows */}
      <div className="divide-y divide-background-200/60">
        <div className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-foreground-400 bg-background-100/30">
          Cost breakdown
        </div>
        {costRows.map((cr) => (
          <div key={cr.key} className="flex items-center justify-between px-4 py-2.5">
            <span className="text-[13px] text-foreground-600">{cr.label}</span>
            <span className="text-[13px] tabular-nums font-medium">
              <ValueCell value={r[cr.key]} />
              {cr.key === 'swap' && r.swapNoteMark && (
                <sup className="ml-0.5 text-[9px] font-bold text-secondary-600">{r.swapNoteMark}</sup>
              )}
            </span>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div className="border-t-2 border-foreground-800/60 px-4 py-3 flex items-center justify-between">
        <span className="text-[11px] font-[750] uppercase tracking-[0.07em] text-foreground-950">Total / trade</span>
        <span className="text-[20px] font-[750] text-foreground-950 tabular-nums leading-none">{r.total}</span>
      </div>

      <div className="border-t border-background-200/60 px-4 py-2.5 grid grid-cols-2 gap-x-4">
        <div>
          <div className="text-[10px] text-foreground-400 mb-0.5">Break-even</div>
          <div className="text-[13px] font-medium text-foreground-900 tabular-nums">{r.breakEven}</div>
        </div>
        <div>
          <div className="text-[10px] text-foreground-400 mb-0.5">Profit target</div>
          <div className="text-[13px] font-medium text-foreground-900 tabular-nums">{r.target}</div>
        </div>
      </div>

      {/* Cost burden */}
      <div className="border-t-2 border-foreground-800/60 px-4 pt-3 pb-4">
        <div className="flex items-end justify-between mb-2">
          <div>
            <div className="text-[10px] font-[750] uppercase tracking-[0.07em] text-foreground-950">Cost burden</div>
            <div className="text-[10px] text-foreground-400 mt-0.5 leading-snug">Share of profit target</div>
          </div>
          <div className="text-[22px] font-[750] text-foreground-950 tabular-nums leading-none">{r.burden}</div>
        </div>
        <div className="w-full h-[5px] rounded-full bg-background-200 overflow-hidden">
          <div
            className="h-full rounded-full bg-accent-500 transition-all duration-700"
            style={{ width: `${(r.burdenNum / maxBurden) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export default function PerTradeCostReference() {
  const scalper = rows[0];
  const position = rows[rows.length - 1];

  return (
    <section className="relative w-full bg-background-50 overflow-hidden">
      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-14 md:py-20">

        {/* ========== HEADER ========== */}
        <div className="mb-10 md:mb-14 max-w-[680px]">
          <h2 className="text-[26px] md:text-[42px] font-[750] leading-[1.1] text-foreground-950 tracking-[-0.02em]">
            The same four costs, weighed side by side.
          </h2>
          <p className="mt-3 md:mt-4 text-[14px] md:text-[15px] text-foreground-500 leading-relaxed">
            Monthly totals hide the truth. Read each column top to bottom to see what a
            single trade actually costs — then read the last row across to see who really
            pays the price.
          </p>
        </div>

        {/* ========== MOBILE: CARD LAYOUT ========== */}
        <div className="block md:hidden space-y-4">
          {rows.map((r, i) => (
            <StrategyCard key={i} r={r} index={i} />
          ))}
          {/* Footnotes mobile */}
          {footnotes.length > 0 && (
            <div className="pt-2 space-y-1.5">
              {footnotes.map((f) => (
                <p key={f.mark} className="text-[11px] text-foreground-400 leading-relaxed">
                  <sup className="text-secondary-600 font-bold mr-1">{f.mark}</sup>
                  {f.text}
                </p>
              ))}
              <p className="text-[11px] text-foreground-400 leading-relaxed">
                Bars are scaled relative to the heaviest strategy; percentages show the true share of each target.
              </p>
            </div>
          )}
        </div>

        {/* ========== DESKTOP: TABLE LAYOUT ========== */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-left">
            <thead>
              <tr className="align-bottom">
                <th className="w-[190px] pb-6 pr-6"></th>
                {rows.map((r, i) => (
                  <th key={i} className="pb-6 px-5 text-left border-l border-background-200/60">
                    <div className="text-[15px] md:text-[16px] font-[750] text-foreground-950 leading-tight">
                      {r.strategy}
                    </div>
                    <div className="text-[12px] text-foreground-500 mt-1">{r.trader}</div>
                    <div className="text-[12px] text-foreground-400">{r.lotSize} / trade</div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              <tr>
                <td colSpan={rows.length + 1} className="pt-2 pb-3 border-t-2 border-foreground-800/80">
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-foreground-400">
                    Cost breakdown
                  </span>
                </td>
              </tr>

              {costRows.map((cr, ri) => (
                <tr key={ri} className="border-t border-background-200/60">
                  <td className="py-3.5 pr-6 text-[13px] font-medium text-foreground-600">
                    {cr.label}
                  </td>
                  {rows.map((r, ci) => (
                    <td key={ci} className="py-3.5 px-5 text-[14px] border-l border-background-200/60">
                      <ValueCell value={r[cr.key]} />
                      {cr.key === 'swap' && r.swapNoteMark && (
                        <sup className="ml-0.5 text-[10px] font-bold text-secondary-600">
                          {r.swapNoteMark}
                        </sup>
                      )}
                    </td>
                  ))}
                </tr>
              ))}

              <tr className="border-t-2 border-foreground-800/80">
                <td className="py-5 pr-6 text-[13px] font-[750] uppercase tracking-[0.06em] text-foreground-950">
                  Total per trade
                </td>
                {rows.map((r, ci) => (
                  <td key={ci} className="py-5 px-5 border-l border-background-200/60">
                    <span className="text-[22px] md:text-[26px] font-[750] text-foreground-950 tabular-nums leading-none">
                      {r.total}
                    </span>
                  </td>
                ))}
              </tr>

              <tr className="border-t border-background-200/60">
                <td className="py-3.5 pr-6 text-[13px] font-medium text-foreground-600">
                  Break-even
                </td>
                {rows.map((r, ci) => (
                  <td key={ci} className="py-3.5 px-5 text-[14px] text-foreground-900 tabular-nums border-l border-background-200/60">
                    {r.breakEven}
                  </td>
                ))}
              </tr>
              <tr className="border-t border-background-200/60">
                <td className="py-3.5 pr-6 text-[13px] font-medium text-foreground-600">
                  Profit target
                </td>
                {rows.map((r, ci) => (
                  <td key={ci} className="py-3.5 px-5 text-[14px] text-foreground-900 tabular-nums border-l border-background-200/60">
                    {r.target}
                  </td>
                ))}
              </tr>

              <tr className="border-t-2 border-foreground-800/80">
                <td className="pt-5 pb-2 pr-6 align-top">
                  <div className="text-[13px] font-[750] uppercase tracking-[0.06em] text-foreground-950">
                    Cost burden
                  </div>
                  <div className="text-[11px] text-foreground-400 mt-1 leading-snug">
                    Share of the profit target eaten by costs
                  </div>
                </td>
                {rows.map((r, ci) => (
                  <td key={ci} className="pt-5 pb-2 px-5 align-top border-l border-background-200/60">
                    <div className="text-[24px] md:text-[28px] font-[750] text-foreground-950 tabular-nums leading-none mb-2.5">
                      {r.burden}
                    </div>
                    <div className="w-full h-[6px] rounded-full bg-background-200 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-accent-500 transition-all duration-700"
                        style={{ width: `${(r.burdenNum / maxBurden) * 100}%` }}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>

          {/* Footnotes desktop */}
          {footnotes.length > 0 && (
            <div className="mt-6 space-y-1.5">
              {footnotes.map((f) => (
                <p key={f.mark} className="text-[12px] text-foreground-400 leading-relaxed">
                  <sup className="text-secondary-600 font-bold mr-1">{f.mark}</sup>
                  {f.text}
                </p>
              ))}
              <p className="text-[12px] text-foreground-400 leading-relaxed">
                Bars in the cost-burden row are scaled relative to the heaviest strategy;
                percentages show the true share of each target.
              </p>
            </div>
          )}
        </div>

        {/* ========== THE PARADOX ========== */}
        <div className="mt-16 md:mt-28">
          <div className="flex items-center gap-4 mb-8 md:mb-10">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-foreground-400 whitespace-nowrap">
              The Paradox
            </span>
            <div className="flex-1 h-px bg-background-200/70"></div>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-20">
            {/* Left: narrative */}
            <div className="lg:w-[420px] flex-shrink-0">
              <p className="text-[20px] md:text-[27px] font-[700] text-foreground-950 leading-[1.2] tracking-[-0.01em] mb-3 md:mb-4">
                The more you pay, the less it hurts.
              </p>
              <p className="text-[13px] md:text-[15px] text-foreground-600 leading-relaxed">
                A scalper needs just 1.0 pip to break even — but that is 20% of a 5-pip
                target, gone before profit begins. A position trader needs 16.1 pips, yet
                that is only 5.4% of a 300-pip move. Absolute cost climbs with time; relative
                burden shrinks. That trade-off is baked into every strategy you choose.
              </p>
            </div>

            {/* Right: two anchor comparison */}
            <div className="flex-1 min-w-0 w-full">
              <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:gap-x-10">
                {/* Scalper */}
                <div>
                  <div className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.12em] text-foreground-400 mb-2 md:mb-3">
                    {scalper.strategy} — cheapest ticket
                  </div>
                  <div className="flex items-baseline gap-1.5 md:gap-2 mb-1">
                    <span className="text-[24px] md:text-[36px] font-[750] text-foreground-950 tabular-nums leading-none">
                      {scalper.total}
                    </span>
                    <span className="text-[11px] text-foreground-400">per trade</span>
                  </div>
                  <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-background-200/60">
                    <div className="text-[28px] md:text-[44px] font-[750] text-accent-600 tabular-nums leading-none">
                      {scalper.burden}
                    </div>
                    <div className="text-[11px] md:text-[12px] text-foreground-500 mt-1 md:mt-1.5 leading-snug">
                      of a {scalper.target} target — the heaviest drag on the board
                    </div>
                  </div>
                </div>

                {/* Position */}
                <div>
                  <div className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.12em] text-foreground-400 mb-2 md:mb-3">
                    {position.strategy} — priciest ticket
                  </div>
                  <div className="flex items-baseline gap-1.5 md:gap-2 mb-1">
                    <span className="text-[24px] md:text-[36px] font-[750] text-foreground-950 tabular-nums leading-none">
                      {position.total}
                    </span>
                    <span className="text-[11px] text-foreground-400">per trade</span>
                  </div>
                  <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-background-200/60">
                    <div className="text-[28px] md:text-[44px] font-[750] text-foreground-900 tabular-nums leading-none">
                      {position.burden}
                    </div>
                    <div className="text-[11px] md:text-[12px] text-foreground-500 mt-1 md:mt-1.5 leading-snug">
                      of a {position.target} target — the lightest of the long-hold styles
                    </div>
                  </div>
                </div>
              </div>

              {/* Bridge line */}
              <div className="mt-6 md:mt-8 pt-5 md:pt-6 border-t border-background-200/60">
                <p className="text-[12px] md:text-[14px] text-foreground-600 leading-relaxed">
                  <span className="font-[700] text-foreground-900">73× the dollar cost</span> — yet
                  nearly <span className="font-[700] text-foreground-900">4× lighter</span> as a
                  share of the target. And swing trading proves the trap runs both ways: cheap at
                  a 1-day hold (2.6%), its burden climbs to 8.6% by day seven as swap accumulates
                  in silence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}